/**
 * 铃铛通知入口 - 消息中心面板
 * @author ritaxxu
 */
import React, { useCallback, useEffect, useState } from 'react'
import { Badge, Button, Tabs, Empty, Spin, message, Radio } from 'antd'
import { BellOutlined, ArrowRightOutlined } from '@ant-design/icons'
import { useHistory } from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'
import { notificationStore } from '@store'
import * as notificationService from '@services/notification'
import {
  IInnerMsgItem,
  InnerMsgLevel,
  InnerMsgReadStatus,
  IGetInnerMsgListReq,
} from '@types'
import MessageCard from './MessageCard'
import MessageDetailModal from './MessageDetailModal'
import './index.less'

// 浮窗仅承载未读站内信：全部未读 / 重要未读
type TabKey = 'unread' | 'important'

const NotificationBell: React.FC = () => {
  const {
    unreadCount,
    panelVisible,
    togglePanel,
    closePanel,
    fetchUnreadCount,
    startPolling,
  } = notificationStore(
    useShallow((s) => ({
      unreadCount: s.unreadCount,
      panelVisible: s.panelVisible,
      togglePanel: s.togglePanel,
      closePanel: s.closePanel,
      fetchUnreadCount: s.fetchUnreadCount,
      startPolling: s.startPolling,
    })),
  )

  const history = useHistory()

  const [activeTab, setActiveTab] = useState<TabKey>('unread')
  const [activeCategory, setActiveCategory] = useState<string>('all')
  const [messages, setMessages] = useState<IInnerMsgItem[]>([])
  const [loading, setLoading] = useState(false)
  const [detailVisible, setDetailVisible] = useState(false)
  const [currentMessage, setCurrentMessage] = useState<IInnerMsgItem | null>(null)

  // 消息类别选项，value 与后端返回的 tag 文案一致，'all' 表示不过滤
  const categoryOptions = [
    { value: 'all', label: '全部' },
    { value: '线索派发', label: '线索派发' },
    { value: '客户分配', label: '客户分配' },
    { value: '线索通知', label: '线索通知' },
  ]

  // 按类目对当前消息列表做本地过滤
  const filteredMessages
    = activeCategory === 'all'
      ? messages
      : messages.filter((m) => m.tags?.includes(activeCategory))

  // 各分类在当前 tab 下的数量，用于分类按钮上的计数
  const getCategoryCount = (value: string) =>
    value === 'all'
      ? messages.length
      : messages.filter((m) => m.tags?.includes(value)).length

  // 跳转消息中心查看全量（含已读）站内信
  const goToMessageCenter = useCallback(() => {
    closePanel()
    history.push('/notification')
  }, [closePanel, history])

  // 启动轮询
  useEffect(() => {
    startPolling()
  }, [startPolling])

  // 面板打开时加载消息，同时把类目筛选重置为「全部」
  useEffect(() => {
    if (panelVisible) {
      setActiveCategory('all')
      loadMessages(activeTab)
    }
  }, [panelVisible])

  // 切换 tab 时也把类目筛选重置为「全部」，并重新拉列表
  useEffect(() => {
    if (!panelVisible) return
    setActiveCategory('all')
    loadMessages(activeTab)
  }, [activeTab])

  const loadMessages = async (tab: TabKey) => {
    setLoading(true)
    // 浮窗只展示未读，两个 tab 都强制 readStatus=Unread；重要 tab 追加 level 过滤
    const params: IGetInnerMsgListReq = {
      page: 1,
      pageSize: 20,
      readStatus: InnerMsgReadStatus.Unread,
    }
    if (tab === 'important') {
      params.level = InnerMsgLevel.Important
    }
    const [data] = await notificationService.getInnerMsgList(params)
    setMessages(data?.list || [])
    setLoading(false)
  }

  // 标为已读：普通/重要都走 MarkInnerMsgRead(recipientIds=[id])
  // 阅读时间以后端为准：标记成功后重新拉列表（已读消息会从未读浮窗移除），
  // 需要查看阅读时间可进入消息中心
  const doMarkRead = useCallback(async (msg: IInnerMsgItem) => {
    await notificationService.markInnerMsgRead({ recipientIds: [msg.id] })
    fetchUnreadCount()
    loadMessages(activeTab)
  }, [fetchUnreadCount, activeTab])

  // 点击卡片 -> 弹窗详情；普通消息自动已读，重要消息需要用户手动点【标为已读】
  const handleClickMessage = useCallback(async (msg: IInnerMsgItem) => {
    setCurrentMessage(msg)
    setDetailVisible(true)
    if (msg.readStatus === InnerMsgReadStatus.Unread && msg.level === InnerMsgLevel.Normal) {
      await doMarkRead(msg)
    }
  }, [doMarkRead])

  // 卡片右侧【标为已读】按钮 / 详情弹窗【标为已读】按钮
  const handleMarkAsRead = useCallback(async (msg: IInnerMsgItem) => {
    await doMarkRead(msg)
  }, [doMarkRead])

  // 清除普通未读（后端强制 level=1，重要消息不允许批量清除）
  const handleClearNormalUnread = useCallback(async () => {
    await notificationService.markInnerMsgRead({ level: InnerMsgLevel.Normal })
    fetchUnreadCount()
    message.success('已清除普通未读消息')
    loadMessages(activeTab)
  }, [activeTab, fetchUnreadCount])

  const tabItems = [
    {
      key: 'unread',
      label: (
        <span>
          全部未读
          {unreadCount.unreadCount > 0 && (
            <Badge count={unreadCount.unreadCount} size="small" style={{ marginLeft: 4 }} />
          )}
        </span>
      ),
    },
    {
      key: 'important',
      label: (
        <span>
          重要未读
          {unreadCount.importantUnreadCount > 0 && (
            <Badge count={unreadCount.importantUnreadCount} size="small" style={{ marginLeft: 4 }} />
          )}
        </span>
      ),
    },
  ]

  return (
    <div className="notification-bell-wrapper">
      <Badge count={unreadCount.unreadCount} offset={[-4, 4]}>
        <BellOutlined className="notification-bell-icon" onClick={togglePanel} />
      </Badge>

      {panelVisible && (
        <>
          <div className="notification-mask" onClick={closePanel} />
          <div className="notification-panel">
            <div className="notification-panel-header">
              <h3>未读消息</h3>
              <Button
                type="link"
                size="small"
                disabled={unreadCount.unreadCount - unreadCount.importantUnreadCount <= 0}
                onClick={handleClearNormalUnread}
              >
                清除普通未读
              </Button>
            </div>

            <Tabs
              activeKey={activeTab}
              onChange={(key) => setActiveTab(key as TabKey)}
              items={tabItems}
              className="notification-tabs"
            />

            <div className="notification-category-filter">
              <Radio.Group
                value={activeCategory}
                buttonStyle="outline"
                onChange={(e) => setActiveCategory(e.target.value)}
              >
                {categoryOptions.map((cat) => (
                  <Radio.Button key={cat.value} value={cat.value}>
                    {cat.label} ({getCategoryCount(cat.value)})
                  </Radio.Button>
                ))}
              </Radio.Group>
            </div>

            <div className="notification-panel-body">
              <Spin spinning={loading}>
                {filteredMessages.length === 0 ? (
                  <Empty
                    className="notification-empty"
                    description={
                      <>
                        暂无未读站内信，
                        <br />
                        可进入消息中心查看全部站内信
                      </>
                    }
                  />
                ) : (
                  filteredMessages.map((msg) => (
                    <MessageCard
                      key={msg.id}
                      message={msg}
                      onClick={() => handleClickMessage(msg)}
                      onMarkAsRead={() => handleMarkAsRead(msg)}
                    />
                  ))
                )}
              </Spin>
            </div>

            <div className="notification-panel-footer" onClick={goToMessageCenter}>
              进入消息中心
              <ArrowRightOutlined className="notification-panel-footer-arrow" />
            </div>
          </div>
        </>
      )}

      <MessageDetailModal
        visible={detailVisible}
        message={currentMessage}
        onClose={() => setDetailVisible(false)}
        onMarkRead={() => {
          if (currentMessage) {
            handleMarkAsRead(currentMessage)
          }
        }}
      />
    </div>
  )
}

export default NotificationBell
